import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { compact, remove, sortBy as _sortBy } from 'lodash'
import * as dateFns from 'date-fns'

import Api from 'services/Api'
import { Format } from 'services/Dates'
import { closeModal, openModal } from 'store/actions/modals'
import { createErrorNotification } from 'store/actions/notifications'
import { destroy, init, setSelection } from 'store/actions/tables'
import BaseModal from 'components/Modals/BaseModal'
import { Button } from 'components/Forms'
import { FilterBar, Table } from 'components/UI'
import { UserAttributes, UserOccupations } from 'Manager/Users/components'
import { exportUsersAsCSV } from 'Manager/Users/userFunctions'
import { ButtonGroup, Card, Dropdown } from 'ui'

import { t } from 'translations'

const searchOptions = ['attributes', 'departments', 'hiringData', 'teams', 'positions', 'quickAssignOptions', 'timeOffOnlyCheck', 'timeOfDontCheck']

const ScheduleQuickAssignModal = () => {
  const dispatch = useDispatch()

  const { baseDate } = useSelector(state => state.events)
  const { enableOccupations, enableAttributes, eventSortField, sortEventsByEmployeeName } = useSelector(state => state.settings)
  const { 'schedule-quick-assign': tableOptions = {} } = useSelector(state => state.tables)
  const { usersById } = useSelector(state => state.resources)

  const [isLoading, setIsLoading] = useState()
  const isDisabled = !Object.keys(tableOptions.selectedResourcesMap || {}).length

  useEffect(() => {
    const fetchAvailableUsers = async ({ filter = {}, sortBy }) => {
      const { flag = [], vacationTypeNot = [], vacationType = [], ...remainingFilters } = filter
      setIsLoading(true)
      // Reset the selection when we change the filter
      setSelection('schedule-quick-assign', {})
      const params = {
        ...remainingFilters,
        fields: { firstName: 1, lastName: 1, _id: 1, specificRestrictions: 1, occupations: 1, attributes: 1 },
        flagConfig: {
          dates: [Format(baseDate, { outFormat: 'date' })],
          flags: { archivedUser: true, ...flag.reduce((acc, flag) => ({ ...acc, [flag]: true }), {}) },
          options: { checkSpecificTimeOff: vacationType, checkSpecificTimeOffNot: vacationTypeNot }
        },
        sortBy,
        useNewReturn: true
      }
      const { result, success } = await Api.post(`/users/flags`, params)
      setIsLoading(false)
      if (!success) {
        dispatch(createErrorNotification())
        return [[], 0]
      }

      // Filters out the non eligible users
      remove(result, employee => {
        const dateFlag = Format(baseDate, { outFormat: 'flagDate' })
        return employee.flags.dateFlags[dateFlag].isEligible === false || employee.flags.otherFlags.isEligible === false
      })

      const sortedResult = _sortBy(result, eventSortField && sortEventsByEmployeeName ? eventSortField : 'firstName')
      return [sortedResult, result.length]
    }

    const header = [
      { key: 'fullName', label: 'NAME' },
      enableOccupations
        ? {
            key: 'occupations',
            label: 'manager.users.OCCUPATIONS',
            type: 'custom',
            render: occupations => <UserOccupations occupations={occupations} />
          }
        : null,
      enableAttributes
        ? {
            key: 'attributes',
            label: 'manager.users.ATTRIBUTES',
            type: 'custom',
            render: (attributes = []) => <UserAttributes attributes={attributes} />
          }
        : null,
      {
        key: 'specificRestrictions',
        label: 'manager.users.SPECIFIC_RESTRICTIONS',
        type: 'custom',
        render: restrictions => (restrictions ? restrictions.join(', ') : null)
      }
    ]

    init('schedule-quick-assign', compact(header), fetchAvailableUsers, { isSelectable: true, limit: Infinity })
    return () => destroy('schedule-quick-assign')
  }, [baseDate, dispatch, enableAttributes, enableOccupations, eventSortField, sortEventsByEmployeeName])

  const createShifts = () => {
    const selectedUsers = Object.keys(tableOptions.selectedResourcesMap)
    dispatch(closeModal())
    dispatch(
      openModal('ScheduleMultiEditModal', {
        newEvents: selectedUsers.map(employeeId => ({
          employeeId,
          start: Format(dateFns.set(dateFns.startOfDay(baseDate), { hours: 8 })),
          end: Format(dateFns.set(dateFns.startOfDay(baseDate), { hours: 16 })),
          breaks: []
        }))
      })
    )
  }

  const createTimeOff = () => {
    const selectedUsers = Object.keys(tableOptions.selectedResourcesMap)
    dispatch(closeModal())
    dispatch(
      openModal('TimeOffMultiCreateModal', {
        userIds: selectedUsers
      })
    )
  }

  return (
    <BaseModal title="manager.schedule.QUICK_ASSIGN" hideButtons noPadding>
      <div className="p-3">
        <FilterBar options={searchOptions} contentName="schedule-quick-assign" heroFilters={['departments', 'attributes']} />
      </div>

      <Card maxHeight="40rem" className="mx-3 mb-3">
        <Table name="schedule-quick-assign" />
      </Card>
      <div className="px-3 pb-3">
        <ButtonGroup className="mr-2">
          <Button className="btn btn-primary" disabled={isDisabled} isSubmitting={isLoading} onClick={createShifts}>
            {t('manager.schedule.QUICK_ASSIGN.PRIMARY_ACTION')}
          </Button>
          <Dropdown.Toggle color="primary" id="creation-dropdown" disabled={isDisabled} />
          <Dropdown.Menu>
            <Dropdown.Item onClick={createTimeOff}>{t('manager.schedule.QUICK_ASSIGN.CREATE_TIME_OFF')}</Dropdown.Item>
          </Dropdown.Menu>
        </ButtonGroup>

        <Button
          className="btn btn-outline-secondary mr-2"
          disabled={isDisabled}
          isSubmitting={isLoading}
          onClick={() => exportUsersAsCSV(Object.keys(tableOptions.selectedResourcesMap).map(_id => usersById[_id]))}
        >
          {t('manager.schedule.QUICK_ASSIGN.EXPORT_SELECTION')}
        </Button>
        <Button className="btn btn-outline-secondary" onClick={() => dispatch(closeModal())}>
          {t(`actions.CLOSE`)}
        </Button>
      </div>
    </BaseModal>
  )
}

export default ScheduleQuickAssignModal
